/* eslint-disable @typescript-eslint/no-var-requires */
import { Injectable } from '@angular/core';
import { catchError, map, Observable, Observer, of, switchMap, throwError } from 'rxjs';
import { RbacFileValidation } from '../../../rbacc/models/rbacc.model';
import { HttpClient } from '@angular/common/http';
import { RequiredServicesModel } from '../model/e-id.model';
import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '../../../core/services/config.service';

@Injectable({
  providedIn: 'root'
})
export class FileService {

  constructor(private http: HttpClient, private route: ActivatedRoute, private configService: ConfigService) { }

  get allowedOdxFileExtensions(): string[] {
    return ['odx', 'odx-d'];
    //return ['odx', 'odx-d', 'pdx'];
  }

  get allowedRbacFileExtensions(): string[] {
    return ['xml'];
    //return ['odx', 'odx-d', 'pdx'];
  }

  get allowedYamlFileExtensions(): string[] {
    return ['yml', 'yaml'];
  }

  public isCompatibleOdxFile(fileName: string): boolean {
    const currentOdxFileExtension = fileName.split('.').pop();

    if (!this.allowedOdxFileExtensions.find(allowedExtension => allowedExtension === currentOdxFileExtension)) {
      return false;
    }
    return true;
  }

  public isCompatibleRbacFile(fileName: string): boolean {
    const currentRbacFileExtension = fileName.split('.').pop();

    if (!this.allowedRbacFileExtensions.find(allowedExtension => allowedExtension === currentRbacFileExtension)) {
      return false;
    }

    return true;
  }

  public isCompatibleYmlFile(fileName: string): boolean {
    const currentFileExtension = fileName.split('.').pop();

    if (!this.allowedYamlFileExtensions.find(allowedExtension => allowedExtension === currentFileExtension)) {
      return false;
    }

    return true;
  }


  public getOdxFileContentsFromPdxAsBase64(pdxFile: File): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const zipFileName = pdxFile.name.substr(0, pdxFile.name.lastIndexOf('.')) + '.zip';

      pdxFile.arrayBuffer().then(dataFromPDX => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const blobParts = [new Blob([dataFromPDX])];
        const zipFile = new File(blobParts, zipFileName, { type: 'application/x-zip-compressed' });
        const jsZip = require('jszip');
        // const odxFile = new OdxFile();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        jsZip.loadAsync(zipFile).then((zip: any) => {
          Object.keys(zip.files).forEach((filename) => {
            const fileExtension = filename.split('.').pop();
            if (fileExtension === 'odx-d') {
              zip.files[filename].async('string').then((fileData: File) => {
                this.getOdxFileContentsAsBase64(new Blob([fileData])).subscribe(
                  data => {
                    observer.next(data);
                    observer.complete();
                  }
                );
              });
            }
          });
        });
      });
    });
  }

  public getOdxFileContentsAsBase64(file: Blob): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const fileReader = new FileReader();
      fileReader.readAsBinaryString(file);
      fileReader.onloadend = () => {
        if (fileReader.result !== null) {
          observer.next(btoa(fileReader.result.toString()));
          observer.complete();
        } else {
          observer.error("Failed to read file.");
        }
      };
      fileReader.onerror = () => {
        observer.error("Failed to read file.");
      };
    });
  }


  public getRbacFileContentsAsBase64(file: Blob): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const fileReader = new FileReader();
      fileReader.readAsBinaryString(file);
      fileReader.onloadend = () => {
        if (fileReader.result !== null) {
          observer.next(btoa(fileReader.result.toString()));
          observer.complete();
        } else {
          observer.error("Failed to read file.");
        }
      };
    });
  }

  public validateRbacFile(rbacFile: { fileName: string, fileData: File | null }): Observable<RbacFileValidation> {
    if (!this.isCompatibleRbacFile(rbacFile.fileName)) {
      return of({
        isValidFile: false,
        isValidContent: true,
        validationMessage: `The selected file is not a valid Rbacc file. Only files with extension.xml are allowed`
      })
    }
    return of({ isValidFile: true, isValidContent: true, validationMessage: `` })
  }

  public validateOdxFile(odxFile: { fileName: string, fileData: File }, specId: number): Observable<{ isValidFile: boolean, isValidContent: boolean, validationMessage: string }> {

    if (!this.isCompatibleOdxFile(odxFile.fileName)) {
      return of({ isValidFile: false, isValidContent: false, validationMessage: `The selected file is not a valid ODX file. Only files with extension ODX or ODX-d are allowed` })
    }
    return this.validateOdxFileContents(odxFile.fileData, specId);
  }

  public validateOdxFileContents(fileData: File, specId: number): Observable<{ isValidFile: boolean, isValidContent: boolean, validationMessage: string }> {
    return this.getOdxFileContentsAsBase64(fileData).pipe(
      switchMap(fileAsBase64 => {
        return this.getRequiredDiagnosticServiceList(specId).pipe(
          // If backend returns 404 for “no required services”, treat as empty list
          catchError(err => {
            if (err.status === 404) {
              return of({ items: [] as { serviceName: string; serviceCode?: string }[] });
            }
            return throwError(() => err);
          }),
          map(requiredServices => {
            const result = { isValidFile: true, isValidContent: true, validationMessage: '<ul>' };
            const odxFileData = atob(fileAsBase64);

            const servicesInOdx = this.getServicesInOdxFile(odxFileData);
            for (const req of (requiredServices.items ?? [])) {
              if (!servicesInOdx.includes(req.serviceName)) {
                result.isValidContent = false;
                result.validationMessage += `<li>${req.serviceName} ${req.serviceCode ?? ''} is missing.</li>`;
              }
            }
            result.validationMessage += '</ul>';
            return result;
          })
        );
      })
    );
  }

  public getRequiredDiagnosticServiceList(specificationId: number | null): Observable<RequiredServicesModel> {
    return this.http.get<RequiredServicesModel>(this.configService.apiUrl + 'services/required/' + specificationId);
  }

  private getServicesInOdxFile(odxFileData: string): Array<string> {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(odxFileData, "text/xml");
    const nodes = xmlDoc.querySelectorAll('DIAG-SERVICE SHORT-NAME');
    const serviceNames: string[] = [];
    nodes.forEach(node => serviceNames.push(node.innerHTML));

    return serviceNames;
  }
}
